/**
 * Safety Reports Page — Admin management of pilot safety reports
 * Filter by status, review details, update status with admin notes.
 */

import { useState, useEffect } from 'react';
import { api } from '@/services/api';
import { useToast } from '@/contexts/ToastContext';
import {
  ShieldExclamationIcon,
  XMarkIcon,
  CheckCircleIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import type { SafetyReport, SafetyStats, SafetyReportStatus } from '@/types/safety';
import {
  HAZARD_LABELS,
  SEVERITY_COLORS,
  STATUS_BADGE_CLASSES,
  STATUS_LABELS,
  getRiskLevel,
  RISK_LEVEL_CLASSES,
} from '@/types/safety';

type TabFilter = 'ALL' | SafetyReportStatus;

const TABS: { key: TabFilter; label: string }[] = [
  { key: 'ALL', label: 'All' },
  { key: 'OPEN', label: 'Open' },
  { key: 'UNDER_REVIEW', label: 'Under Review' },
  { key: 'CLOSED', label: 'Closed' },
];

export default function SafetyReportsPage() {
  const { showSuccess, showError } = useToast();

  const [reports, setReports] = useState<SafetyReport[]>([]);
  const [stats, setStats] = useState<SafetyStats>({ open: 0, underReview: 0, closed: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabFilter>('ALL');
  const [selectedReport, setSelectedReport] = useState<SafetyReport | null>(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      const query = activeTab === 'ALL' ? '' : `?status=${activeTab}`;
      const [reportsRes, statsRes] = await Promise.allSettled([
        api.get<{ data: SafetyReport[] }>(`/safety-reports${query}`),
        api.get<{ data: SafetyStats }>('/safety-reports/stats'),
      ]);
      if (reportsRes.status === 'fulfilled') setReports(reportsRes.value.data);
      if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
    } catch {
      showError('Load failed', 'Could not load safety reports');
    } finally {
      setLoading(false);
    }
  };

  const openDetail = (report: SafetyReport) => {
    setSelectedReport(report);
    setAdminNotes(report.adminNotes || '');
  };

  const closeDetail = () => {
    setSelectedReport(null);
    setAdminNotes('');
  };

  const handleStatusUpdate = async (newStatus: SafetyReportStatus) => {
    if (!selectedReport) return;
    if (newStatus === 'CLOSED' && !adminNotes.trim()) {
      showError('Notes required', 'Please add admin notes before closing a report');
      return;
    }
    setUpdating(true);
    try {
      await api.patch(`/safety-reports/${selectedReport.id}/status`, {
        status: newStatus,
        adminNotes: adminNotes.trim() || undefined,
      });
      showSuccess(`Report ${newStatus === 'UNDER_REVIEW' ? 'moved to review' : 'closed'}`);
      closeDetail();
      await loadData();
    } catch {
      showError('Update failed', 'Could not update report status');
    } finally {
      setUpdating(false);
    }
  };

  const formatDate = (d: string) => {
    return new Date(d).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const tabCount = (key: TabFilter): number => {
    if (key === 'ALL') return stats.total;
    if (key === 'OPEN') return stats.open;
    if (key === 'UNDER_REVIEW') return stats.underReview;
    return stats.closed;
  };

  if (loading && reports.length === 0) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <ShieldExclamationIcon className="w-7 h-7 text-amber-500" />
              Safety Reports
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              {stats.open + stats.underReview > 0
                ? `${stats.open + stats.underReview} report${stats.open + stats.underReview !== 1 ? 's' : ''} requiring attention`
                : 'No reports requiring attention'}
            </p>
          </div>
          {stats.open > 0 && (
            <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 text-sm font-medium">
              {stats.open} open
            </span>
          )}
        </div>

        {/* Stats bar */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          {([
            { label: 'Open', value: stats.open, cls: 'text-red-600 dark:text-red-400' },
            { label: 'Under Review', value: stats.underReview, cls: 'text-amber-600 dark:text-amber-400' },
            { label: 'Closed', value: stats.closed, cls: 'text-green-600 dark:text-green-400' },
          ] as const).map((s) => (
            <div key={s.label} className="bg-card border border-border rounded-lg px-4 py-3 text-center">
              <p className={`text-2xl font-bold ${s.cls}`}>{s.value}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-4 bg-muted/30 rounded-lg p-1">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                activeTab === tab.key
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.label}
              <span className="ml-1.5 text-xs opacity-60">({tabCount(tab.key)})</span>
            </button>
          ))}
        </div>

        {/* Report list */}
        {reports.length === 0 ? (
          <div className="bg-card border border-border rounded-lg px-5 py-16 text-center">
            <CheckCircleIcon className="w-12 h-12 text-green-500 mx-auto mb-3" />
            <h3 className="text-lg font-medium text-foreground">
              {activeTab === 'ALL' ? 'No safety reports' : `No ${STATUS_LABELS[activeTab as SafetyReportStatus].toLowerCase()} reports`}
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              {activeTab === 'ALL'
                ? 'Safety reports filed by pilots will appear here.'
                : 'Try a different filter tab.'}
            </p>
          </div>
        ) : (
          <div className="bg-card border border-border rounded-lg overflow-hidden divide-y divide-border">
            {reports.map((report) => {
              const risk = getRiskLevel(report.severity, report.likelihood);
              return (
                <div
                  key={report.id}
                  onClick={() => openDetail(report)}
                  className="px-5 py-4 hover:bg-muted/30 cursor-pointer transition-colors"
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2.5">
                      <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${SEVERITY_COLORS[report.severity]}`} />
                      <span className="text-sm font-medium text-foreground">
                        {HAZARD_LABELS[report.hazardCategory]}
                      </span>
                      <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded uppercase ${RISK_LEVEL_CLASSES[risk]}`}>
                        {risk}
                      </span>
                    </div>
                    <span className={`px-2 py-0.5 text-xs rounded-full ${STATUS_BADGE_CLASSES[report.status]}`}>
                      {STATUS_LABELS[report.status]}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground truncate mb-1.5">{report.narrative}</p>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    {report.trip && <span className="font-mono">{report.trip.qNumber}</span>}
                    {report.fromAirport && report.toAirport && (
                      <span>{report.fromAirport} → {report.toAirport}</span>
                    )}
                    {report.tailNumber && <span>{report.tailNumber}</span>}
                    <span>
                      {report.anonymous ? 'Anonymous' : report.reporter ? `${report.reporter.firstName} ${report.reporter.lastName}` : ''}
                    </span>
                    <span className="ml-auto">{formatDate(report.createdAt)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Detail modal */}
      {selectedReport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-card border border-border rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Modal header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-card rounded-t-xl">
              <div className="flex items-center gap-3">
                <span className={`w-3 h-3 rounded-full ${SEVERITY_COLORS[selectedReport.severity]}`} />
                <h2 className="text-lg font-semibold text-foreground">
                  {HAZARD_LABELS[selectedReport.hazardCategory]}
                </h2>
                <span className={`px-2 py-0.5 text-xs rounded-full ${STATUS_BADGE_CLASSES[selectedReport.status]}`}>
                  {STATUS_LABELS[selectedReport.status]}
                </span>
              </div>
              <button onClick={closeDetail} className="p-1.5 rounded-md hover:bg-muted transition-colors">
                <XMarkIcon className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-5">
              {/* Flight context */}
              {(selectedReport.trip || selectedReport.fromAirport || selectedReport.tailNumber) && (
                <div className="bg-muted/30 rounded-lg px-4 py-3">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Flight Context</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {selectedReport.trip && (
                      <div>
                        <span className="text-muted-foreground">Trip: </span>
                        <span className="font-mono font-medium text-foreground">{selectedReport.trip.qNumber}</span>
                      </div>
                    )}
                    {selectedReport.tailNumber && (
                      <div>
                        <span className="text-muted-foreground">Aircraft: </span>
                        <span className="font-medium text-foreground">{selectedReport.tailNumber}</span>
                      </div>
                    )}
                    {selectedReport.fromAirport && selectedReport.toAirport && (
                      <div>
                        <span className="text-muted-foreground">Route: </span>
                        <span className="font-medium text-foreground">{selectedReport.fromAirport} → {selectedReport.toAirport}</span>
                      </div>
                    )}
                    {selectedReport.flightDate && (
                      <div>
                        <span className="text-muted-foreground">Date: </span>
                        <span className="font-medium text-foreground">{formatDate(selectedReport.flightDate)}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Reporter */}
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Reporter</p>
                <p className="text-sm text-foreground">
                  {selectedReport.anonymous
                    ? 'Anonymous'
                    : selectedReport.reporter
                      ? `${selectedReport.reporter.firstName} ${selectedReport.reporter.lastName}`
                      : 'Unknown'}
                </p>
              </div>

              {/* Narrative */}
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Narrative</p>
                <p className="text-sm text-foreground whitespace-pre-wrap">{selectedReport.narrative}</p>
              </div>

              {/* Action taken */}
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Action Taken</p>
                <p className="text-sm text-foreground whitespace-pre-wrap">{selectedReport.actionTaken}</p>
              </div>

              {/* Recommended follow-up */}
              {selectedReport.recommendedFollowUp && (
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Recommended Follow-Up</p>
                  <p className="text-sm text-foreground whitespace-pre-wrap">{selectedReport.recommendedFollowUp}</p>
                </div>
              )}

              {/* Risk assessment */}
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Risk Assessment</p>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-muted-foreground">
                    Severity: <span className="font-medium text-foreground capitalize">{selectedReport.severity.toLowerCase()}</span>
                  </span>
                  <span className="text-muted-foreground">&times;</span>
                  <span className="text-sm text-muted-foreground">
                    Likelihood: <span className="font-medium text-foreground capitalize">{selectedReport.likelihood.toLowerCase()}</span>
                  </span>
                  <span className="text-muted-foreground">=</span>
                  {(() => {
                    const risk = getRiskLevel(selectedReport.severity, selectedReport.likelihood);
                    return (
                      <span className={`px-2 py-0.5 text-xs font-semibold rounded-full uppercase ${RISK_LEVEL_CLASSES[risk]}`}>
                        {risk} risk
                      </span>
                    );
                  })()}
                </div>
              </div>

              {/* Timestamps */}
              <div className="flex items-center gap-4 text-xs text-muted-foreground pt-2 border-t border-border">
                <span>Filed: {formatDate(selectedReport.createdAt)}</span>
                {selectedReport.closedAt && <span>Closed: {formatDate(selectedReport.closedAt)}</span>}
                {selectedReport.reviewedBy && (
                  <span>Reviewed by: {selectedReport.reviewedBy.firstName} {selectedReport.reviewedBy.lastName}</span>
                )}
              </div>

              {/* Admin notes */}
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Admin Notes</p>
                {selectedReport.status === 'CLOSED' ? (
                  <p className="text-sm text-foreground whitespace-pre-wrap bg-muted/30 rounded-lg px-4 py-3">
                    {selectedReport.adminNotes || 'No notes'}
                  </p>
                ) : (
                  <textarea
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    placeholder="Add investigation notes, corrective actions, or follow-up items..."
                    rows={3}
                    className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 resize-y"
                  />
                )}
              </div>

              {/* Actions */}
              {selectedReport.status !== 'CLOSED' && (
                <div className="flex items-center gap-3 pt-2">
                  {selectedReport.status === 'OPEN' && (
                    <button
                      onClick={() => handleStatusUpdate('UNDER_REVIEW')}
                      disabled={updating}
                      className="flex items-center gap-1.5 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                    >
                      {updating ? (
                        <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                      ) : (
                        <MagnifyingGlassIcon className="w-4 h-4" />
                      )}
                      Start Review
                    </button>
                  )}
                  {selectedReport.status === 'UNDER_REVIEW' && (
                    <button
                      onClick={() => handleStatusUpdate('CLOSED')}
                      disabled={updating}
                      className="flex items-center gap-1.5 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                    >
                      {updating ? (
                        <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                      ) : (
                        <CheckCircleIcon className="w-4 h-4" />
                      )}
                      Close Report
                    </button>
                  )}
                  <button
                    onClick={closeDetail}
                    className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
